import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Scale, FileText, Shield, AlertTriangle, CreditCard, Mail } from "lucide-react";

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Home</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-100 mb-6">
            <Scale className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Terms of Service</h1>
          <p className="text-slate-500">Last Updated: December 2024</p>
        </div>

        {/* Policy Content */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-8 md:p-12 space-y-10">
            
            {/* 1. Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">1</span>
                Introduction
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>
                  Welcome to <strong>CLARIAS</strong>, an AI-powered educational support platform designed to assist users in their UPSC preparation journey.
                </p>
                <p>
                  By accessing, browsing, or using the CLARIAS platform (including website and mobile applications), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service ("Terms").
                </p>
                <p>
                  CLARIAS reserves the right to update or modify these Terms at any time. Continued use of the platform after any modifications constitutes your acceptance of the revised Terms. We encourage you to review these Terms periodically.
                </p>
              </div>
            </section>

            {/* 2. Nature of Service */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">2</span>
                Nature of Service
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>CLARIAS is an <strong>AI-powered educational support platform</strong> that provides:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>AI-generated doubt resolution and explanations</li>
                  <li>Practice tests and performance analytics</li>
                  <li>Study material recommendations</li>
                  <li>Progress tracking and behavioural insights</li>
                </ul>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                  <p className="text-amber-800 font-medium">Important Clarifications:</p>
                  <ul className="list-disc pl-6 mt-2 text-amber-700 space-y-1">
                    <li>CLARIAS is <strong>NOT</strong> a coaching institute</li>
                    <li>CLARIAS does <strong>NOT</strong> provide human tutoring or mentorship</li>
                    <li>CLARIAS does <strong>NOT</strong> guarantee exam results, ranks, or selection</li>
                    <li>All services are purely digital and AI-assisted in nature</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 3. UPSC Disclaimer */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">3</span>
                UPSC Disclaimer
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-red-800 font-medium mb-2">Official Disclaimer:</p>
                  <ul className="list-disc pl-6 text-red-700 space-y-2">
                    <li>
                      <strong>CLARIAS is a private, independent company</strong> and is NOT affiliated with, endorsed by, or connected to the Union Public Service Commission (UPSC), Government of India, or any government body.
                    </li>
                    <li>
                      UPSC question papers referenced on this platform are publicly available documents. All explanations, analyses, and interpretations are independently generated by CLARIAS and do not represent official UPSC guidance.
                    </li>
                    <li>
                      CLARIAS is not affiliated with any coaching institute, educational body, or examination authority.
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 4. AI-Based Assistance Disclaimer */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">4</span>
                AI-Based Assistance Disclaimer
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                {/* EXACT TEXT AS REQUIRED BY LEGAL PROMPT */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-amber-800 font-medium">
                    "All responses on CLARIAS are AI-generated and provided for guidance only. Users should apply their own judgement while preparing for exams."
                  </p>
                </div>
                
                <p className="mt-4">CLARIAS utilises artificial intelligence to provide educational assistance. Users must understand that:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>All doubt resolution, content analysis, answer evaluations, and recommendations are <strong>AI-generated</strong></li>
                  <li>AI outputs are intended as <strong>guidance tools</strong> and should not be treated as authoritative or infallible decisions</li>
                  <li>Users are solely responsible for their final preparation strategies, study decisions, and exam performance</li>
                  <li>While we strive for accuracy, AI-generated content may occasionally contain errors or require verification</li>
                </ul>
                <p className="mt-4 text-slate-500 italic">
                  CLARIAS recommends users cross-reference important information with official UPSC notifications and established study resources.
                </p>
              </div>
            </section>

            {/* 5. User Obligations */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">5</span>
                User Obligations
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>By using CLARIAS, you agree to the following obligations:</p>
                <p className="font-medium text-slate-800">Users must NOT:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Copy, reproduce, redistribute, or resell any platform content without explicit written permission</li>
                  <li>Attempt to reverse engineer, decompile, or extract source code from the platform</li>
                  <li>Misuse AI-generated outputs for commercial purposes or misrepresent them as human-created content</li>
                  <li>Use the platform for any unlawful, fraudulent, or malicious purposes</li>
                  <li>Share account credentials or allow unauthorised access to their subscription</li>
                  <li>Engage in any activity that disrupts or interferes with platform functionality</li>
                </ul>
                <p className="mt-4">
                  Violation of these obligations may result in immediate termination of your account and access to CLARIAS services.
                </p>
              </div>
            </section>

            {/* 6. Subscriptions & Payments */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">6</span>
                Subscriptions & Payments
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <ul className="list-disc pl-6 space-y-2">
                  <li>CLARIAS offers both free and paid subscription tiers</li>
                  <li>Paid subscriptions unlock advanced features, increased usage limits, and premium functionalities</li>
                  <li>All pricing, subscription validity periods, and feature inclusions are clearly displayed before purchase</li>
                  <li>Payments are processed through secure third-party payment gateway (Razorpay)</li>
                  <li>Subscription purchases do not guarantee any specific exam outcome, rank, or success</li>
                </ul>
                <div className="bg-slate-100 rounded-xl p-4 mt-4">
                  <p className="text-slate-700">
                    By completing a purchase, you acknowledge that you have reviewed the subscription details and agree to the pricing and terms applicable at the time of purchase.
                  </p>
                </div>
              </div>
            </section>

            {/* 7. Limitation of Liability */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">7</span>
                Limitation of Liability
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>To the maximum extent permitted by applicable law, CLARIAS and its affiliates, officers, employees, and partners shall NOT be liable for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Any exam outcomes, results, ranks, or selection decisions</li>
                  <li>User decisions made based on AI-generated guidance or recommendations</li>
                  <li>Technical issues, service interruptions, or data loss beyond reasonable control</li>
                  <li>Third-party actions, including payment gateway failures</li>
                  <li>Any indirect, incidental, special, or consequential damages</li>
                </ul>
                <p className="mt-4">
                  In no event shall CLARIAS' total liability exceed the amount paid by the user for the subscription in the preceding 12 months.
                </p>
              </div>
            </section>

            {/* 8. Modifications */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">8</span>
                Modifications to Service & Terms
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>CLARIAS reserves the right to, at any time and without prior notice:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Modify, update, or discontinue platform features or functionalities</li>
                  <li>Update subscription pricing for future purchases</li>
                  <li>Change service offerings, content, or availability</li>
                  <li>Amend these Terms of Service</li>
                </ul>
                <p className="mt-4">
                  Significant changes will be communicated through the platform or registered email. Continued use after modifications constitutes acceptance of updated terms.
                </p>
              </div>
            </section>

            {/* 9. Governing Law */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">9</span>
                Governing Law & Jurisdiction
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>
                  These Terms of Service shall be governed by and construed in accordance with the <strong>laws of India</strong>.
                </p>
                <p>
                  Any disputes arising from or relating to these Terms or your use of CLARIAS shall be subject to the exclusive jurisdiction of the courts in India.
                </p>
              </div>
            </section>

            {/* 10. Contact */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold">10</span>
                Contact Us
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>For any questions, concerns, or requests regarding these Terms of Service, please contact us at:</p>
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center gap-3">
                  <Mail className="w-5 h-5 text-indigo-600" />
                  <a href="mailto:support@clarias.in" className="text-indigo-600 font-medium hover:underline">
                    support@clarias.in
                  </a>
                </div>
              </div>
            </section>

          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link to="/privacy-policy" className="text-indigo-600 hover:underline text-sm">Privacy Policy</Link>
          <span className="text-slate-300">|</span>
          <Link to="/refund-policy" className="text-indigo-600 hover:underline text-sm">Refund Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
